import { Anchor, Button } from '@cluey/cluey-components';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useHistory } from 'react-router';

import { api } from '../../../../../api';
import type { CreateStudentParams } from '../../../../../api/services/student';
import { Student } from '../../../../../api/types/student';
import AlertExclamationIcon from '../../../../../assets/images/icon-filled-alert-exclamation-circle.svg';
import CheckTickIcon from '../../../../../assets/images/icon-outline-check-tick.svg';
import { ShadowContainer } from '../../../../../components/ShadowContainer';
import { FormFlowProps } from '../../../../../types/hubInterfaces';
import { convertHubToSfLearningNeeds } from '../../../../../util/learning-needs';
import { PATH_HUB_CR_ADD_ENROLMENT, PATH_HUB_HOME } from '../../../../../util/pagePath';
import { useSetPageHeader } from '../../../../../v2/app-shell/usePageHeader';
import { ClueyPlusSpinner } from '../../../../../v2/common/ClueyPlusLoader';
import { cn } from '../../../../../v2/common/helpers/cn';
import { Checkbox } from '../../../../../v2/components/ui/checkbox';

interface Props {
  formFlowProps: FormFlowProps;
}

type HistoryState = {
  enrolStudent?: boolean;
  firstName: string;
  lastName: string;
  yearLevel: string;
  location?: string;
  schoolName?: string;
};

const AddStudentClueyPlus = ({ formFlowProps }: Props) => {
  const formUtils = useFormContext();
  const history = useHistory<HistoryState>();
  const enrolStudent = history.location.state?.enrolStudent;

  const { firstName, lastName, yearLevel, location } = formUtils.getValues();

  const { mutate: createStudent, isLoading: creatingStudent } = api.student.create.useMutation();
  const { mutate: signUpNewStudent, isLoading: isSigningUpNewStudent } = api.clueyPlus.signUpNewStudent.useMutation();
  const { data: accountDetails } = api.account.details.useQuery();
  const { refetch: refetchStudents } = api.student.all.useQuery();
  const { refetch: refetchStudentSubscription } = api.subscription.getAll.useQuery();

  const loading =
    creatingStudent || isSigningUpNewStudent || (formUtils.formState.isSubmitted && !formUtils.formState.errors.root);

  const [agree, setAgree] = useState(false);
  const [termsAndConditionsError, setTermsAndConditionsError] = useState<string | null>(null);

  const isNZ = accountDetails?.country === 'NZ';
  const privacyPolicyLink = isNZ
    ? 'https://clueylearning.co.nz/privacy-policy/'
    : 'https://clueylearning.com.au/privacy-policy/';

  // Layout effect so the back button is in the first paint, including the
  // view-transition snapshot taken while navigating here.
  useSetPageHeader({
    label: 'Students',
    showIcon: false,
    showBackButton: true,
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const subscriptionDescription = [
    'Instant help when feeling stuck',
    'Make learning less stressful, more rewarding',
    'Build confidence with every step',
  ];

  const pollStudentSubscription = (attempts: number, newStudent: Student | undefined) => {
    if (attempts >= 20) {
      history.push(PATH_HUB_HOME);
    }
    setTimeout(async () => {
      const { data } = await refetchStudentSubscription();
      const newSubs = data.find((subs) => subs.studentId === newStudent.studentId);
      if (!!newSubs) {
        if (enrolStudent) {
          history.push(PATH_HUB_CR_ADD_ENROLMENT, {
            firstName,
            lastName,
            yearLevel,
            location,
          });
        } else {
          formFlowProps.next({ newStudent, newSubs });
        }

        return;
      }
      pollStudentSubscription(attempts + 1, newStudent);
    }, 5000);
  };

  const pollStudentsApi = (attempts: number) => {
    if (attempts >= 20) {
      history.push(PATH_HUB_HOME);
    }
    setTimeout(async () => {
      const { data } = await refetchStudents();
      const newStudent = data.find(
        (student) => student.firstName === firstName && lastName === student.lastName && yearLevel === student.yearLevel
      );
      if (!!newStudent) {
        signUpNewStudent(
          {
            accountId: accountDetails?.customerSfid,
            countryCode: accountDetails?.country,
            students: [
              {
                sfid: newStudent?.studentId,
                firstName: newStudent?.firstName,
                lastName: newStudent?.lastName,
                yearLevel: newStudent?.currentYearLevel,
                state: accountDetails?.country === 'NZ' ? '' : newStudent?.location,
                postcode: '',
              },
            ],
          },
          {
            onSuccess: () => {
              pollStudentSubscription(0, newStudent);
            },
          }
        );
        return;
      }
      pollStudentsApi(attempts + 1);
    }, 5000);
  };

  const submit = formUtils.handleSubmit(async () => {
    formUtils.clearErrors('root');
    const { firstName, lastName, yearLevel, schoolName, specialLearningNeeds, location } = formUtils.getValues();
    const params: CreateStudentParams = {
      firstname: firstName,
      lastname: lastName,
      curriculum: location,
      yearLevel,
      schoolId: schoolName,
      specialLearningNeeds: !isEmpty(specialLearningNeeds)
        ? convertHubToSfLearningNeeds(specialLearningNeeds).join(';')
        : null,
    };
    createStudent(params, {
      onSuccess: async () => {
        pollStudentsApi(0);
      },
      onError: (error) => {
        formUtils.setError('root', {
          message: `There was an error encountered adding the student. Please try again. ${error}`,
        });
      },
    });
  });

  const onConfirm = () => {
    if (!agree) {
      setTermsAndConditionsError('Accept terms and conditions to continue');
      return;
    }

    void submit();
  };

  return (
    <div className="container pb-5 md:pb-0">
      <ShadowContainer className="flex flex-col gap-6 !rounded-3xl bg-white p-6 md:!p-10">
        <div className="flex flex-row justify-start">
          <h2 className="merriweather text-lg font-bold md:text-2xl">Cluey+</h2>
          <span className="ml-3 flex items-center rounded-full bg-green-3 px-2 py-1 text-center text-xs font-semibold">
            Try for free
          </span>
        </div>
        <div className="flex flex-row justify-start">
          <p>Full access to Cluey+ for 30 days</p>
        </div>
        <div className="flex flex-col gap-y-4">
          {subscriptionDescription.map((msg) => (
            <div className="flex flex-row gap-x-2" key={msg}>
              <img src={CheckTickIcon} alt="check tick icon" className="h-4 w-4" />
              <p className="text-sm">{msg}</p>
            </div>
          ))}
        </div>
        <div className="mb-1 w-full pt-5 md:w-[50%]">
          By continuing, I consent that Cluey collects, uses and discloses my personal information in accordance with
          Cluey's
          <Anchor
            className="ml-[0.25rem] text-orange-600 underline"
            href={privacyPolicyLink}
            size="base"
            target="_blank"
          >
            Privacy Policy
          </Anchor>
          .
        </div>
        <div>
          <div className="mb-1 flex flex-row items-start gap-3 md:items-center md:gap-0">
            <div className="mt-1 flex h-5 w-8 items-center md:mt-0">
              <Checkbox
                id="signup-agree"
                className={`h-5 w-5 ${termsAndConditionsError ? '!border-[3px] !border-red-400' : ''}`}
                disabled={loading}
                checked={agree}
                aria-invalid={!!termsAndConditionsError}
                onCheckedChange={(checked) => {
                  setAgree(checked);
                  setTermsAndConditionsError('');
                }}
              />
            </div>
            <label
              htmlFor="signup-agree"
              className={cn('!mb-0 !font-normal', loading ? 'text-grey-200' : 'text-grey-600')}
            >
              I have read and agree to Cluey+
              <Anchor
                className="ml-1 !text-orange-600 underline"
                href={
                  isNZ
                    ? 'https://clueylearning.co.nz/cluey-plus-agreement/'
                    : 'https://clueylearning.com.au/cluey-plus-agreement/'
                }
                size="base"
                target="_blank"
              >
                Terms and Conditions
              </Anchor>
            </label>
          </div>
          {termsAndConditionsError && (
            <div className="mt-3 flex flex-row items-start gap-3 md:items-center">
              <span className="mt-1 flex h-5 w-6 items-center md:mt-0 md:w-5">
                <img src={AlertExclamationIcon} className="!h-5 !w-5" alt="error-icon" />
              </span>
              <span className="font-semibold text-red-400">{termsAndConditionsError}</span>
            </div>
          )}
        </div>
        {loading ? (
          <div className="flex flex-row items-center gap-3">
            <ClueyPlusSpinner />
            <p className="mt-0 font-medium !text-[#393D41]">Creating student profile. This may take a few minutes.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4 pt-3 md:flex-row md:justify-start">
            <Button onClick={onConfirm} className="flex h-10 items-center justify-center gap-3 normal-case" size="sm">
              <span>Confirm</span>
            </Button>
            <Button
              onClick={() => {
                history.push(PATH_HUB_HOME);
              }}
              className="flex h-10 items-center justify-center !border normal-case"
              size="sm"
              appearance="reverse"
            >
              <span className="text-base font-medium tracking-wide text-grey-400">Cancel</span>
            </Button>
          </div>
        )}
      </ShadowContainer>
    </div>
  );
};

export { AddStudentClueyPlus };
